import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const stappen = [
  { icon: 'ri-lightbulb-line', step: '01', title: 'Deel uw idee of product', desc: 'Stuur ons uw concept, inspiratiebeelden of bestaand product. Wij denken direct mee.' },
  { icon: 'ri-pencil-ruler-2-line', step: '02', title: 'Ontwerp & sampling', desc: 'Onze fabriekspartners maken een sample op basis van uw briefing. U geeft feedback.' },
  { icon: 'ri-check-double-line', step: '03', title: 'Goedkeuring & productie', desc: 'Na uw goedkeuring start de productie. Wij bewaken kwaliteit en voortgang.' },
  { icon: 'ri-truck-line', step: '04', title: 'DDP levering aan uw deur', desc: 'All-in levering inclusief transport, douane en BTW. Geen extra verrassingen.' },
];

const mogelijkheden = [
  { icon: 'ri-t-shirt-line', title: 'Kleding & textiel', desc: 'T-shirts, hoodies, workwear, tassen — met uw logo en kleuren.' },
  { icon: 'ri-gift-2-line', title: 'Verpakkingen & tassen', desc: 'Kraft tassen, giftboxes, cadeautasjes, bedrukt papier — volledig op maat.' },
  { icon: 'ri-medicine-bottle-line', title: 'Beauty & cosmetica', desc: 'White label cosmetica, skincare, verpakkingen voor beauty merken.' },
  { icon: 'ri-cup-line', title: 'Horeca & food packaging', desc: 'Bekers, dozen, zakken, verpakkingen met uw huisstijl.' },
  { icon: 'ri-smartphone-line', title: 'Elektronica & accessoires', desc: 'Private label elektronica, gadgets, powerbanks, oordopjes.' },
  { icon: 'ri-award-line', title: 'Promotieartikelen', desc: 'Pennen, flyers, banners, eventmaterialen met uw branding.' },
  { icon: 'ri-store-line', title: 'Retail producten', desc: 'Eigen productlijn voor uw winkel of webshop — volledig gebranded.' },
  { icon: 'ri-settings-2-line', title: 'Maatwerk productie', desc: 'Staat uw product er niet bij? Wij sourcen vrijwel alles op maat.' },
];

const faqs = [
  { q: 'Wat is private label producten laten maken in China?', a: 'Private label betekent dat u bestaande producten inkoopt en voorziet van uw eigen merk, logo en verpakking. De fabriek produceert het product, u verkoopt het onder uw eigen naam.' },
  { q: 'Wat is het verschil tussen OEM en ODM productie uit China?', a: 'OEM (Original Equipment Manufacturer) = u levert het ontwerp, de fabriek produceert het. ODM (Original Design Manufacturer) = de fabriek heeft een bestaand design dat u aanpast met uw branding. DUC regelt beide.' },
  { q: 'Hoe hoog is de minimale bestelhoeveelheid voor private label China?', a: 'Dit verschilt per product. Voor veel private label producten is een MOQ van 100–500 stuks mogelijk. Wij zoeken altijd een fabriek die past bij uw volume en budget.' },
  { q: 'Hoe lang duurt het om een private label product te laten produceren?', a: 'Inclusief sampling en goedkeuring: 4–8 weken productie + levertijd. Wij geven bij elk project een realistische planning.' },
  { q: 'Kan ik mijn eigen verpakking laten ontwerpen voor mijn private label product?', a: 'Ja. DUC regelt volledig maatwerk verpakkingen: kleur, formaat, materiaal, print. Van simpele sticker tot full-colour giftbox — alles is mogelijk.' },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-all overflow-hidden ${open ? 'border-[#3B9EE8]/40 bg-white' : 'border-[#0A1628]/8 bg-white'}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer">
        <span className={`font-500 text-base pr-8 leading-snug ${open ? 'text-[#0A1628] font-600' : 'text-[#0A1628]/80'}`}>{q}</span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${open ? 'bg-[#3B9EE8] rotate-45' : 'bg-[#0A1628]/6'}`}>
          <i className={`ri-add-line text-sm ${open ? 'text-white' : 'text-[#0A1628]/60'}`} />
        </div>
      </button>
      {open && (
        <div className="px-7 pb-6 border-t border-[#3B9EE8]/15">
          <p className="text-[#0A1628]/65 text-base leading-relaxed pt-4">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function PrivateLabelChinaPage() {
  useSEO({
    title: 'Private Label China | Producten Laten Maken met Eigen Logo | DUC',
    description: 'Private label producten laten maken in China via DUC. OEM & ODM productie, eigen logo en verpakking, DDP levering. Van kleding tot beauty — alles op maat. Gratis offerte.',
    keywords: 'private label China, producten laten maken China, OEM ODM China, eigen merk produceren China, white label China, verpakkingen op maat China',
    canonical: `${SITE_URL}/private-label-china`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Private Label China', item: `${SITE_URL}/private-label-china` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Private Label China — Uw eigen merk produceren via DUC',
        description: 'DUC regelt uw private label productie in China van A tot Z. Van ontwerp en sampling tot productie en DDP levering. Bouw uw eigen merk.',
        url: `${SITE_URL}/private-label-china`,
        inLanguage: 'nl-NL',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Wat is private label producten laten maken in China?',
            acceptedAnswer: { '@type': 'Answer', text: 'Private label betekent dat u bestaande producten inkoopt en voorziet van uw eigen merk, logo en verpakking. De fabriek produceert het product, u verkoopt het onder uw eigen naam.' },
          },
          {
            '@type': 'Question',
            name: 'Wat is het verschil tussen OEM en ODM productie uit China?',
            acceptedAnswer: { '@type': 'Answer', text: 'OEM (Original Equipment Manufacturer) = u levert het ontwerp, de fabriek produceert het. ODM (Original Design Manufacturer) = de fabriek heeft een bestaand design dat u aanpast met uw branding. DUC regelt beide.' },
          },
          {
            '@type': 'Question',
            name: 'Hoe hoog is de minimale bestelhoeveelheid voor private label China?',
            acceptedAnswer: { '@type': 'Answer', text: 'Dit verschilt per product. Voor veel private label producten is een MOQ van 100–500 stuks mogelijk. Wij zoeken altijd een fabriek die past bij uw volume en budget.' },
          },
          {
            '@type': 'Question',
            name: 'Kan ik mijn eigen verpakking laten ontwerpen voor mijn private label product?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ja. DUC regelt volledig maatwerk verpakkingen: kleur, formaat, materiaal, print. Van simpele sticker tot full-colour giftbox — alles is mogelijk.' },
          },
        ],
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInView(stepsRef, { once: true, margin: '-80px' });

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b70a28c9793de377a0b4dc1d90b5f92f.jpg"
            alt="Private label producten laten maken in China — DUC"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/70 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/45 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line" />
              Terug naar home
            </Link>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-paint-brush-line" />
            Private Label China &bull; OEM &amp; ODM productie
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 max-w-3xl">
            Private label producten<br />
            <span className="text-[#3B9EE8]">laten maken in China</span><br />
            <span className="text-2xl md:text-3xl font-600 text-white/70">Uw merk, onze productie</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/60 text-lg max-w-2xl leading-relaxed mb-8">
            DUC regelt uw <strong className="text-white/85">private label productie in China</strong> van A tot Z. Van ontwerp en sampling tot productie en <strong className="text-white/85">DDP levering</strong>. Bouw uw eigen merk met producten die gemaakt zijn bij de beste Chinese fabrieken.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.35 }} className="flex flex-wrap gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-7 py-3.5 rounded-lg font-700 text-sm transition-all cursor-pointer whitespace-nowrap">
              Start uw private label traject
              <i className="ri-arrow-right-line" />
            </Link>
            <a href="https://wa.me/31624359162?text=Hi%2C%20ik%20wil%20graag%20een%20private%20label%20product%20laten%20maken%20in%20China." target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-6 py-3.5 rounded-lg font-500 text-sm transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-base" />
              WhatsApp ons
            </a>
          </motion.div>
        </div>
      </section>

      {/* Mogelijkheden */}
      <section className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Wat kunnen wij als <span className="text-[#3B9EE8]">private label produceren?</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-xl mx-auto">Van cosmetica tot horeca-verpakkingen — DUC regelt vrijwel elk product met uw eigen branding.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {mogelijkheden.map((m, i) => (
              <motion.div key={m.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.06 }} className="bg-white border border-[#0A1628]/6 rounded-xl p-6 hover:border-[#3B9EE8]/35 transition-all">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 mb-4">
                  <i className={`${m.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <h3 className="font-heading font-700 text-[#0A1628] text-sm mb-2">{m.title}</h3>
                <p className="text-[#0A1628]/50 text-xs leading-relaxed">{m.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/voor-wie/overig" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap hover:text-[#2D85C5]">
              Staat uw product er niet bij? Bekijk wat nog meer mogelijk is
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stappenplan */}
      <section ref={stepsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Hoe werkt <span className="text-[#3B9EE8]">private label productie in China?</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-xl mx-auto">Van idee tot uw eigen branded product op uw deur — wij regelen het complete traject.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stappen.map((s, i) => (
              <motion.div key={s.step} initial={{ opacity: 0, y: 24 }} animate={stepsInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.1 }} className="relative">
                <div className="bg-[#F7F8FA] rounded-2xl p-7 border border-[#0A1628]/5 h-full relative overflow-hidden">
                  <span className="absolute top-5 right-6 font-heading font-900 text-[#0A1628]/6 text-5xl leading-none pointer-events-none select-none">{s.step}</span>
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 mb-5">
                    <i className={`${s.icon} text-[#3B9EE8] text-xl`} />
                  </div>
                  <h3 className="font-heading font-700 text-[#0A1628] text-base mb-2">{s.title}</h3>
                  <p className="text-[#0A1628]/55 text-sm leading-relaxed">{s.desc}</p>
                </div>
                {i < stappen.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center text-[#3B9EE8]/50">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* USP Banner */}
      <section className="py-14 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { v: 'OEM & ODM', l: 'Productie opties' },
              { v: 'Low MOQ', l: 'Kleine series mogelijk' },
              { v: '100%', l: 'Custom branding' },
              { v: 'DDP', l: 'All-in levering' },
            ].map((s) => (
              <div key={s.v} className="flex flex-col items-center">
                <span className="font-heading font-800 text-white text-2xl">{s.v}</span>
                <span className="text-[#3B9EE8] text-sm mt-1">{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F7F8FA]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl mb-3">
              Vragen over <span className="text-[#3B9EE8]">private label China?</span>
            </h2>
          </div>
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Start uw eigen <span className="text-[#3B9EE8]">private label merk</span>
          </h2>
          <p className="text-white/55 text-base mb-8 max-w-xl mx-auto">Vraag een gratis en vrijblijvende prijsindicatie aan. Binnen 24 uur weet u wat uw private label product kost bij productie in China.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all cursor-pointer whitespace-nowrap">
              Vraag gratis prijsindicatie aan
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/diensten/private-label" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-7 py-4 rounded-lg font-600 transition-all cursor-pointer whitespace-nowrap">
              Meer over onze dienst
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
